import { Country } from '@afterpay/types'
import { useFlag } from '@afterpaytouch/feature'
import { Cohort, TermsDisclosureStatus } from '@afterpaytouch/portal-api'
import { AddressMode, Features } from '../../model'
import {
  useCheckoutState,
  useCohort,
  useCountry,
  useCreditCheckConsented,
  useExperiment,
  useGetCreditCheckConsent,
  useGetTermsDisclosureStatus,
  useHasBillingAgreements,
  useIsBillingAgreementApprovalProcess,
  useIsDeferredShipping,
  useIsEstimatedPPA,
  useIsExpress,
  useShowPreferredPaymentDay,
  useUkRegFlags,
} from '../../state'
import { useLateFeeDetails } from './helper'

export interface TermsContext {
  country: Country
  isExpress: boolean
  isBuyNow: boolean
  addressMode?: AddressMode
  isDeferredShipping: boolean
  isZeroLateFee: boolean
  hasEstimatedPPA: boolean
  shouldCreditCheck: boolean
  hasBillingAgreements: boolean
  isBillingAgreementApprovalProcess: boolean
  hasPartialLateFeesEnabled: boolean
  preferredDayShown: boolean
  isUkRegulationEnabled: boolean
  isUkMarketRegsFCA: boolean
  isNZCreditCheckEnabled: boolean
  nzTermsEnabled: boolean
  showTermsDisclosure: boolean
  hasCreditCheckConsent?: boolean
}

export const useTermsContext = (): TermsContext => {
  const country = useCountry()
  const { initial } = useCheckoutState()
  const isExpress = useIsExpress()
  const isDeferredShipping = useIsDeferredShipping()
  const cohort = useCohort()
  const enableBillingAgreementForCashCohort = useFlag(Features.ENABLE_BA_CASH_COHORT, false)
  const shouldShowBillingAgreementTerms = cohort === Cohort.CashOnly ? enableBillingAgreementForCashCohort : true
  const hasBillingAgreements = useHasBillingAgreements() && shouldShowBillingAgreementTerms
  const { isZeroLateFee } = useLateFeeDetails()
  const hasEstimatedPPA = useIsEstimatedPPA()
  const isCreditCheckConsented = useCreditCheckConsented()
  const shouldCreditCheck = isCreditCheckConsented !== undefined && !isCreditCheckConsented && country === Country.US && cohort !== Cohort.CashOnly
  const hasPartialLateFeesEnabled = useFlag(Features.PARTIAL_LATE_FEES)
  const preferredDayShown = useShowPreferredPaymentDay()
  const { isUkMarketRegsFCA, isUkRegulationEnabled } = useUkRegFlags()
  const hasCreditCheckConsent = useGetCreditCheckConsent()
  const nzCreditCheck = useExperiment('NzCreditCheck')
  const isNZCreditCheckEnabled = nzCreditCheck.active && !nzCreditCheck.control && country === Country.NZ
  const nzTermsEnabled = useFlag(Features.NZ_UPDATED_TERMS)
  const termsDisclosureStatus = useGetTermsDisclosureStatus()
  const showTermsDisclosure = termsDisclosureStatus === TermsDisclosureStatus.NEW_USER_NOT_ACCEPTED_TERMS && country === Country.NZ
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  return {
    country,
    isExpress,
    isBuyNow: initial.buyNow,
    addressMode: initial?.addressMode,
    isDeferredShipping,
    isZeroLateFee,
    hasEstimatedPPA,
    shouldCreditCheck,
    hasBillingAgreements,
    isBillingAgreementApprovalProcess,
    hasPartialLateFeesEnabled,
    preferredDayShown,
    isUkRegulationEnabled,
    isUkMarketRegsFCA,
    isNZCreditCheckEnabled,
    nzTermsEnabled,
    showTermsDisclosure,
    hasCreditCheckConsent,
  }
}
